import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Route, Link as LinkIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import type { TripsResponse } from "../../lib/types";
import { fmtDateTime } from "../../lib/utils";
import { Card, FilterChip } from "../core/Layout";

export function VehicleTrips({ vehicleId }: { vehicleId: string }) {
  const { user } = useAuth();
  const [days, setDays] = useState(7);
  const { data, isLoading } = useQuery<TripsResponse>({ queryKey: ["vehicle-trips", vehicleId, days], queryFn: async () => (await api.get(`/telematics/vehicle/${vehicleId}/trips`, { params: { days } })).data, refetchInterval: 120000 });
  const base = user?.user_type === "super_admin" ? "/bo" : "/app";
  return (
    <Card title="Trips" icon={Route} testId="vehicle-trips-card" actions={<div className="flex gap-1">{[1, 7, 30].map((d) => <FilterChip key={d} active={days === d} onClick={() => setDays(d)} testId={`trips-range-${d}`}>{d === 1 ? "Today" : `${d} days`}</FilterChip>)}</div>}>
      {isLoading ? <p className="text-sm text-slate-400">Loading trips…</p> : !data?.connected || !data.linked ? (
        <p className="text-sm text-slate-500" data-testid="trips-unavailable">{!data?.connected ? "Connect Geotab to see trip history." : "This vehicle is not linked to a Geotab device yet."} <Link to={`${base}/telematics/devices`} className="inline-flex items-center gap-1 font-semibold text-[color:var(--brand)] hover:underline"><LinkIcon className="h-3 w-3" />Manage devices</Link></p>
      ) : (
        <>
          <p className="mb-3 text-xs text-slate-500" data-testid="trips-summary"><span className="font-semibold text-slate-900">{data.trips.length}</span> trip(s) · <span className="font-semibold text-slate-900">{data.total_km} km</span> · {Math.floor(data.total_minutes / 60)}h {data.total_minutes % 60}m driving{data.mock ? " · mock sandbox" : ""}</p>
          {data.trips.length === 0 ? <p className="text-sm text-slate-500">No trips recorded in this period.</p> : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px]" data-testid="trips-table">
                <thead><tr className="border-b border-slate-200"><th className="th pl-0">Start</th><th className="th">End</th><th className="th text-right">Distance</th><th className="th text-right">Driving</th><th className="th text-right">Idle</th><th className="th text-right">Max / avg</th></tr></thead>
                <tbody>{data.trips.map((t) => (
                  <tr key={t.id} className="row-hover" data-testid={`trip-row-${t.id}`}>
                    <td className="td pl-0 text-xs">{fmtDateTime(t.start)}</td><td className="td text-xs">{fmtDateTime(t.stop)}</td>
                    <td className="td text-right font-semibold">{t.distance_km} km</td><td className="td text-right text-xs">{t.driving_minutes} min</td><td className="td text-right text-xs">{t.idling_minutes} min</td>
                    <td className="td text-right text-xs">{t.max_speed_kmh} / {t.avg_speed_kmh} km/h</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
